"use client";
import SectionTitle from "@/shared/app/ui/text/PageSectionTitle";
import { motion } from "framer-motion";
import Image from "next/image";

interface AppStepCardProps {
  title: string;
  description: string;
  image: string;
  delay?: number;
}

const AppStepCard: React.FC<AppStepCardProps> = ({
  title,
  description,
  image,
  delay = 0,
}) => {
  return (
    <motion.div
      className="flex flex-col flex-1  rounded-3xl text-center overflow-hidden"
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: "easeOut", delay }}
      viewport={{ once: true, amount: 0.3 }}
    >
      {/* 텍스트 */}
      <div className="mb-8">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: delay + 0.2 }}
          viewport={{ once: true }}
          className="text-center  text-[15px] md:text-lg text-white bg-[#5081FF] inline-flex px-3 md:px-4 py-[5px] md:py-2 rounded-full mb-3 md:mb-4 tracking-tight leading-[21px]"
          style={{ fontFamily: '"pre-Medium", sans-serif' }}
        >
          {title}
        </motion.p>
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: delay + 0.3 }}
          viewport={{ once: true }}
          className="text-sm md:text-base tracking-tight text-midGray-50 md:leading-7 leading-[22px] break-keep whitespace-pre-line"
          style={{ fontFamily: '"pre-Medium", sans-serif' }}
        >
          {description}
        </motion.p>
      </div>

      {/* 이미지 */}
      <motion.div
        className="w-full relative flex justify-center items-center mx-auto aspect-[312/430]"
        initial={{ opacity: 0, scale: 0.9 }}
        whileInView={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8, ease: "easeOut", delay: delay + 0.4 }}
        viewport={{ once: true, amount: 0.3 }}
      >
        <div className="relative w-[70%] md:w-[60%] lg:w-[280px] max-w-full overflow-hidden">
          <Image
            src={image}
            alt={title}
            width={332}
            height={612}
            className="object-contain rounded-2xl w-full h-auto"
          />
        </div>
      </motion.div>
    </motion.div>
  );
};

export default function MovementInvestigateSection() {
  return (
    <div className="bg-[#404040] mt-4 py-10">
      <div className="w-full xl:max-w-[1280px] mx-auto px-3 xl:px-2  md:pt-40 pb-10 pt-3">
        <div className="bg-white rounded-4xl px-5 sm:px-10  md:px-20 py-10 sm:py-15 md:py-30">
          {/* 타이틀 */}
          <SectionTitle
            align="left"
            label="Investigate"
            title={<>편의시설 정보, 쉽게 공유하기</>}
            description={
              <>
                상점 정보를 검색·선택하고,
                <br />
                간단한 평가와 사진으로 편의시설 정보를 공유합니다.
              </>
            }
            colorMode="light"
          />

          {/*  */}
          <div className="flex mt-20 space-x-0 lg:space-y-0 space-y-10 lg:space-x-15 lg:flex-row flex-col">
            <AppStepCard
              title="조사하기"
              description={`조사할 장소의 위치를 확인하고,\n조사할 대상을 선택하세요.`}
              image="/assets/images/page/service/movement/app1.png"
            />
            <AppStepCard
              title="편의정보"
              description={`주출입구, 엘리베이터, 주차장, 화장실 등\n해당 장소의 편의정보를 조사합니다.`}
              image="/assets/images/page/service/movement/app2.png"
              delay={0.2}
            />
            <AppStepCard
              title="사진정보"
              description={`조사 장소의 대표사진과\n내부·외부 모습을 촬영합니다.`}
              image="/assets/images/page/service/movement/app3.png"
              delay={0.4}
            />
          </div>

          {/*  */}
        </div>
      </div>
    </div>
  );
}
